import React, { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "../api";
import { useI18n } from "../i18n";
import useSeo from "../hooks/useSeo";
import {
  Users, Gift, ShieldCheck, Loader2, Mail, Copy, CheckCircle2, XCircle, AlertTriangle,
  BarChart3, Wallet, Smartphone, Coins, KeyRound, Link as LinkIcon, Share2, Tag,
} from "lucide-react";

const PAYMENT_METHODS = [
  { id: "usdt", label: "USDT (TRC20)", icon: Coins, placeholder: "T…" },
  { id: "mobile_money", label: "Mobile Money", icon: Smartphone, placeholder: "+225 07…" },
];

export default function Affiliates() {
  const { t, lang } = useI18n();
  const [searchParams] = useSearchParams();
  const preOrderId = searchParams.get("order_id") || "";
  const preEmail = searchParams.get("email") || "";
  const [tab, setTab] = useState(preOrderId && preEmail ? "signup" : "signup");

  useSeo({
    title: lang === "fr"
      ? "Programme d'affiliation — Dr Trade Scalper Pro | Jusqu'à 20 % de commission"
      : lang === "es"
        ? "Programa de afiliación — Dr Trade Scalper Pro | Hasta 20 % de comisión"
        : lang === "de"
          ? "Partnerprogramm — Dr Trade Scalper Pro | Bis zu 20 % Provision"
          : "Affiliate program — Dr Trade Scalper Pro | Up to 20 % commission",
    description: lang === "fr"
      ? "Devenez affilié Dr Trade et touchez jusqu'à 20 % de commission sur chaque licence vendue. Paiements en USDT ou Mobile Money. Inscription gratuite et rapide."
      : lang === "es"
        ? "Hazte afiliado de Dr Trade y gana hasta el 20 % de comisión por cada licencia vendida. Pagos en USDT o Mobile Money. Inscripción gratis y rápida."
        : lang === "de"
          ? "Werden Sie Dr Trade-Partner und erhalten Sie bis zu 20 % Provision auf jede verkaufte Lizenz. Auszahlung in USDT oder Mobile Money. Kostenlose Anmeldung."
          : "Become a Dr Trade affiliate and earn up to 20 % commission on every licence sold. Payouts in USDT or Mobile Money. Free, fast signup.",
    keywords: lang === "fr"
      ? "programme affiliation EA MT5, commission robot trading, affilié Dr Trade, parrainage MetaTrader, revenu passif trading"
      : "MT5 EA affiliate program, trading robot commission, Dr Trade affiliate, MetaTrader referral, trading passive income",
    path: "/affiliates",
  });
  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 py-12" data-testid="page-affiliates">
      <header className="mb-8">
        <div className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.2em] text-cyan-300 bg-cyan-400/10 border border-cyan-400/20 px-2.5 py-1 rounded-full mb-3">
          <Gift className="h-3 w-3" /> {t("affiliates.hdr_badge")}
        </div>
        <h1 className="text-4xl sm:text-5xl font-bold tracking-tighter mb-3">
          {t("affiliates.hdr_title")}
        </h1>
        <p className="text-base text-zinc-400 max-w-2xl leading-relaxed">
          {t("affiliates.hdr_lead_pre")}<b className="text-cyan-300">{t("affiliates.hdr_lead_emph")}</b>{t("affiliates.hdr_lead_post")}
        </p>
      </header>

      <div className="grid md:grid-cols-3 gap-3 mb-10" data-testid="affiliates-tiers">
        <TierCard range={t("affiliates.tier_1")} rate="10 %" accent="text-cyan-300" />
        <TierCard range={t("affiliates.tier_2")} rate="15 %" accent="text-amber-300" />
        <TierCard range={t("affiliates.tier_3")} rate="20 %" accent="text-emerald-300" />
      </div>

      {/* Tabs */}
      <div className="flex gap-1 mb-6 border-b border-white/[0.06]" data-testid="affiliates-tabs">
        <TabBtn active={tab === "signup"} onClick={() => setTab("signup")} icon={Users} label={t("affiliates.tab_signup")} testId="tab-signup" />
        <TabBtn active={tab === "resend"} onClick={() => setTab("resend")} icon={KeyRound} label={t("affiliates.tab_resend")} testId="tab-resend" />
        <TabBtn active={tab === "dashboard"} onClick={() => setTab("dashboard")} icon={BarChart3} label={t("affiliates.tab_dashboard")} testId="tab-dashboard" />
      </div>

      {tab === "signup" && <SignupForm preOrderId={preOrderId} preEmail={preEmail} />}
      {tab === "resend" && <ResendForm />}
      {tab === "dashboard" && <Dashboard />}
    </div>
  );
}

function TabBtn({ active, onClick, icon: Icon, label, testId }) {
  return (
    <button onClick={onClick} data-testid={testId}
      className={`px-4 py-3 text-sm inline-flex items-center gap-2 border-b-2 transition-colors -mb-px ${
        active ? "border-cyan-400 text-white" : "border-transparent text-zinc-400 hover:text-zinc-200"
      }`}>
      <Icon className="h-4 w-4" /> {label}
    </button>
  );
}

function TierCard({ range, rate, accent }) {
  const { t } = useI18n();
  return (
    <div className="glass rounded-xl p-4">
      <div className="text-[11px] uppercase tracking-wider text-zinc-500 mb-1">{range}</div>
      <div className={`text-3xl font-bold tracking-tighter font-mono ${accent}`}>{rate}</div>
      <div className="text-[11px] text-zinc-500 mt-1">{t("affiliates.tier_per_paid")}</div>
    </div>
  );
}

function SignupForm({ preOrderId = "", preEmail = "" }) {
  const { t } = useI18n();
  const [searchParams] = useSearchParams();
  const utmSource = searchParams.get("utm_source") || "";
  const [form, setForm] = useState({
    full_name: "", email: preEmail, order_id: preOrderId,
    payment_method: "usdt", payment_address: "",
  });
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [err, setErr] = useState("");

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setErr(""); setResult(null); setBusy(true);
    try {
      const { data } = await api.post("/affiliates/signup", {
        ...form,
        full_name: form.full_name.trim(),
        email: form.email.trim().toLowerCase(),
        order_id: form.order_id.trim(),
        payment_address: form.payment_address.trim(),
        utm_source: utmSource || undefined,
      });
      setResult(data);
    } catch (e2) {
      const detail = e2.response?.data?.detail;
      setErr(typeof detail === "string" ? detail : t("affiliates.err_signup"));
    } finally {
      setBusy(false);
    }
  };

  if (result?.ok) {
    return <SuccessCard code={result.code} message={result.message} />;
  }

  return (
    <form onSubmit={submit} className="glass rounded-2xl p-6 sm:p-8" data-testid="affiliate-signup-form">
      <div className="flex items-center gap-2 mb-1">
        <ShieldCheck className="h-4 w-4 text-cyan-400" />
        <h2 className="text-lg font-semibold">{t("affiliates.signup_title")}</h2>
      </div>
      <p className="text-xs text-zinc-500 mb-6 leading-relaxed">
        {t("affiliates.signup_lead")}
      </p>

      <div className="grid sm:grid-cols-2 gap-4 mb-4">
        <Field label={t("affiliates.f_fullname")} required>
          <input type="text" value={form.full_name} onChange={update("full_name")} required
            className="input" placeholder={t("affiliates.f_fullname_ph")} data-testid="affiliate-fullname" />
        </Field>
        <Field label={t("affiliates.f_email")} required>
          <input type="email" value={form.email} onChange={update("email")} required
            className="input" placeholder={t("affiliates.f_email_ph")} data-testid="affiliate-email" />
        </Field>
        <Field label={t("affiliates.f_order_id")} required cols={2}>
          <input type="text" value={form.order_id} onChange={update("order_id")} required
            className="input font-mono" placeholder={t("affiliates.f_order_id_ph")} data-testid="affiliate-order-id" />
        </Field>
      </div>

      <div className="mb-4">
        <div className="text-xs text-zinc-400 mb-2">{t("affiliates.f_pay_method")}</div>
        <div className="grid sm:grid-cols-2 gap-3">
          {PAYMENT_METHODS.map((m) => (
            <button type="button" key={m.id}
              onClick={() => setForm({ ...form, payment_method: m.id })}
              data-testid={`affiliate-pay-${m.id}`}
              className={`text-left p-4 rounded-xl border transition-colors ${
                form.payment_method === m.id
                  ? "bg-cyan-400/10 border-cyan-400/50"
                  : "bg-white/[0.02] border-white/10 hover:border-white/25"
              }`}>
              <m.icon className={`h-4 w-4 mb-2 ${form.payment_method === m.id ? "text-cyan-300" : "text-zinc-400"}`} />
              <div className="text-sm font-semibold">{m.label}</div>
            </button>
          ))}
        </div>
      </div>

      <Field label={form.payment_method === "usdt" ? t("affiliates.f_usdt_addr") : t("affiliates.f_mm_num")} required>
        <input type="text" value={form.payment_address} onChange={update("payment_address")} required
          className="input font-mono"
          placeholder={PAYMENT_METHODS.find((x) => x.id === form.payment_method)?.placeholder}
          data-testid="affiliate-payment-address" />
      </Field>

      {err && (
        <div className="text-xs text-red-300 bg-red-400/10 border border-red-400/30 px-3 py-2 rounded mt-4 flex items-start gap-2"
          data-testid="affiliate-error">
          <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" /> <span>{err}</span>
        </div>
      )}

      <button type="submit" disabled={busy}
        className="btn-primary mt-6 w-full py-3 rounded-lg text-sm font-semibold inline-flex items-center justify-center gap-2"
        data-testid="affiliate-submit">
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <ShieldCheck className="h-4 w-4" />}
        {t("affiliates.btn_join")}
      </button>
    </form>
  );
}

function ResendForm() {
  const { t } = useI18n();
  const [email, setEmail] = useState("");
  const [orderId, setOrderId] = useState("");
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState({ kind: "", text: "", code: "" });

  const submit = async (e) => {
    e.preventDefault();
    setMsg({ kind: "", text: "", code: "" }); setBusy(true);
    try {
      const { data } = await api.post("/affiliates/resend-code", {
        email: email.trim().toLowerCase(),
        order_id: orderId.trim(),
      });
      setMsg({ kind: "ok", text: data.message, code: "" });
    } catch (e2) {
      setMsg({ kind: "error", text: e2.response?.data?.detail || t("affiliates.err_generic"), code: "" });
    } finally { setBusy(false); }
  };

  return (
    <form onSubmit={submit} className="glass rounded-2xl p-6 sm:p-8 max-w-xl" data-testid="affiliate-resend-form">
      <div className="flex items-center gap-2 mb-1">
        <Mail className="h-4 w-4 text-cyan-400" />
        <h2 className="text-lg font-semibold">{t("affiliates.resend_title")}</h2>
      </div>
      <p className="text-xs text-zinc-500 mb-6 leading-relaxed">
        {t("affiliates.resend_lead")}
      </p>
      <div className="space-y-3">
        <Field label={t("affiliates.resend_f_email")} required>
          <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required
            className="input" placeholder={t("affiliates.f_email_ph")} data-testid="resend-email" />
        </Field>
        <Field label={t("affiliates.resend_f_order")}>
          <input type="text" value={orderId} onChange={(e) => setOrderId(e.target.value)}
            className="input font-mono" placeholder={t("affiliates.resend_f_order_ph")}
            data-testid="resend-order-id" />
        </Field>
      </div>
      {msg.text && (
        <div className={`text-xs mt-4 px-3 py-2 rounded border ${
          msg.kind === "ok"
            ? "bg-emerald-400/10 border-emerald-400/30 text-emerald-300"
            : "bg-red-400/10 border-red-400/30 text-red-300"
        }`}>
          {msg.text}
        </div>
      )}
      <button type="submit" disabled={busy}
        className="btn-primary mt-5 w-full py-3 rounded-lg text-sm inline-flex items-center justify-center gap-2"
        data-testid="resend-submit">
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Mail className="h-4 w-4" />}
        {t("affiliates.resend_btn")}
      </button>
    </form>
  );
}

function Dashboard() {
  const { t } = useI18n();
  const [code, setCode] = useState("");
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [data, setData] = useState(null);
  const [subTab, setSubTab] = useState("overview"); // overview | referrals | marketing | payouts

  const load = async (e) => {
    e.preventDefault();
    setErr(""); setBusy(true);
    try {
      const { data } = await api.get(`/affiliates/${encodeURIComponent(code.toUpperCase())}/stats?email=${encodeURIComponent(email)}`);
      setData(data);
    } catch (e2) {
      setErr(e2.response?.data?.detail || t("affiliates.err_creds"));
      setData(null);
    } finally { setBusy(false); }
  };

  if (!data) {
    return (
      <form onSubmit={load} className="glass rounded-2xl p-6 sm:p-8 max-w-xl" data-testid="affiliate-dashboard-auth">
        <div className="flex items-center gap-2 mb-1">
          <BarChart3 className="h-4 w-4 text-cyan-400" />
          <h2 className="text-lg font-semibold">{t("affiliates.auth_title")}</h2>
        </div>
        <p className="text-xs text-zinc-500 mb-6 leading-relaxed">
          {t("affiliates.auth_lead")}
        </p>
        <div className="space-y-3">
          <Field label={t("affiliates.f_code")} required>
            <input type="text" value={code} onChange={(e) => setCode(e.target.value.toUpperCase().replace(/[^A-Z0-9-]/g, ""))}
              required className="input font-mono uppercase tracking-wider"
              placeholder={t("affiliates.f_code_ph")} data-testid="dash-code" />
          </Field>
          <Field label={t("affiliates.f_email_short")} required>
            <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required
              className="input" placeholder={t("affiliates.f_email_ph")} data-testid="dash-email" />
          </Field>
        </div>
        {err && <div className="text-xs text-red-300 mt-3">{err}</div>}
        <button type="submit" disabled={busy}
          className="btn-primary mt-5 w-full py-3 rounded-lg text-sm inline-flex items-center justify-center gap-2"
          data-testid="dash-submit">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <BarChart3 className="h-4 w-4" />}
          {t("affiliates.btn_dashboard")}
        </button>
      </form>
    );
  }

  const a = data.affiliate;
  const origin = typeof window !== "undefined" ? window.location.origin : "";
  const shareLink = `${origin}/?ref=${a.code}`;
  return (
    <div className="space-y-5" data-testid="affiliate-dashboard">
      <div className="flex gap-1 border-b border-white/[0.06] flex-wrap">
        <button onClick={() => setSubTab("overview")}
          className={`px-4 py-2 text-sm -mb-px border-b-2 ${subTab === "overview" ? "border-cyan-400 text-white" : "border-transparent text-zinc-400 hover:text-zinc-200"}`}
          data-testid="dash-subtab-overview">{t("affiliates.subtab_overview")}</button>
        <button onClick={() => setSubTab("referrals")}
          className={`px-4 py-2 text-sm -mb-px border-b-2 inline-flex items-center gap-1.5 ${subTab === "referrals" ? "border-cyan-400 text-white" : "border-transparent text-zinc-400 hover:text-zinc-200"}`}
          data-testid="dash-subtab-referrals">
          <Users className="h-3 w-3" /> {t("affiliates.subtab_referrals")}
        </button>
        <button onClick={() => setSubTab("payouts")}
          className={`px-4 py-2 text-sm -mb-px border-b-2 inline-flex items-center gap-1.5 ${subTab === "payouts" ? "border-cyan-400 text-white" : "border-transparent text-zinc-400 hover:text-zinc-200"}`}
          data-testid="dash-subtab-payouts">
          <Wallet className="h-3 w-3" /> {t("affiliates.subtab_payouts")}
        </button>
        <button onClick={() => setSubTab("marketing")}
          className={`px-4 py-2 text-sm -mb-px border-b-2 inline-flex items-center gap-1.5 ${subTab === "marketing" ? "border-cyan-400 text-white" : "border-transparent text-zinc-400 hover:text-zinc-200"}`}
          data-testid="dash-subtab-marketing">
          <Tag className="h-3 w-3" /> {t("affiliates.subtab_marketing")}
        </button>
      </div>

      {subTab === "marketing" ? (
        <MarketingPanel affiliateCode={a.code} email={email} />
      ) : subTab === "referrals" ? (
        <ReferralsPanel affiliateCode={a.code} email={email} />
      ) : subTab === "payouts" ? (
        <PayoutsPanel affiliateCode={a.code} email={email} pendingXof={data.pending_xof} reload={() => setData(null)} />
      ) : (
        <>
          <div className="glass rounded-2xl p-5">
        <div className="flex items-start justify-between flex-wrap gap-3 mb-4">
          <div>
            <div className="text-[11px] uppercase tracking-wider text-zinc-500 mb-1">{t("affiliates.hello")}</div>
            <div className="text-xl font-semibold">{a.full_name}</div>
            <div className="text-xs text-zinc-500 font-mono mt-1">{a.code}</div>
          </div>
          <CopyCodeBtn code={a.code} />
        </div>
        <div className="border-t border-white/[0.06] pt-4">
          <div className="text-[11px] uppercase tracking-wider text-zinc-500 mb-2 flex items-center gap-1.5">
            <Share2 className="h-3 w-3" /> {t("affiliates.share_link_label")}
          </div>
          <div className="flex flex-col sm:flex-row items-stretch gap-2">
            <code className="flex-1 text-xs font-mono text-zinc-200 bg-black/30 px-3 py-2 rounded break-all" data-testid="dash-share-link">
              {shareLink}
            </code>
            <CopyLinkBtn link={shareLink} />
          </div>
          <p className="text-[11px] text-zinc-500 mt-2">
            {t("affiliates.share_link_note")}
          </p>
        </div>
      </div>
      <div className="grid sm:grid-cols-3 gap-3">
        <Stat label={t("affiliates.stat_clients")} value={a.unique_clients} icon={Users} accent="text-cyan-300" />
        <Stat label={t("affiliates.stat_pending")} value={`${data.pending_xof.toLocaleString("fr-FR")} FCFA`} icon={Wallet} accent="text-amber-300" />
        <Stat label={t("affiliates.stat_paid")} value={`${data.paid_xof.toLocaleString("fr-FR")} FCFA`} icon={CheckCircle2} accent="text-emerald-300" />
      </div>
      <div className="glass rounded-2xl p-5">
        <h3 className="text-base font-semibold mb-4">{t("affiliates.commission_details")}</h3>
        {data.commissions.length === 0 ? (
          <div className="text-sm text-zinc-500 italic">{t("affiliates.no_commission")}</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-zinc-500 text-xs uppercase tracking-wider">
                <tr>
                  <th className="px-3 py-2 text-left">{t("affiliates.col_order")}</th>
                  <th className="px-3 py-2 text-left">{t("affiliates.col_type")}</th>
                  <th className="px-3 py-2 text-left">{t("affiliates.col_client_num")}</th>
                  <th className="px-3 py-2 text-left">{t("affiliates.col_rate")}</th>
                  <th className="px-3 py-2 text-right">{t("affiliates.col_commission")}</th>
                  <th className="px-3 py-2 text-left">{t("affiliates.col_status")}</th>
                  <th className="px-3 py-2 text-left">{t("affiliates.col_date")}</th>
                </tr>
              </thead>
              <tbody>
                {data.commissions.map((c, i) => (
                  <tr key={i} className="border-t border-white/[0.04]">
                    <td className="px-3 py-2 font-mono text-xs">{c.order_id}</td>
                    <td className="px-3 py-2">
                      <span className={`text-[10px] uppercase font-mono px-1.5 py-0.5 rounded ${
                        c.order_type === "renewal" ? "bg-amber-400/15 text-amber-300" : "bg-cyan-400/15 text-cyan-300"
                      }`}>
                        {c.order_type === "renewal" ? t("affiliates.type_renewal") : t("affiliates.type_new")}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-xs">#{c.client_rank}</td>
                    <td className="px-3 py-2">
                      <span className="text-[10px] uppercase font-mono bg-cyan-400/10 text-cyan-300 px-1.5 py-0.5 rounded">
                        {c.rate_percent}%
                      </span>
                    </td>
                    <td className="px-3 py-2 text-right font-mono">{c.commission_xof.toLocaleString("fr-FR")} FCFA</td>
                    <td className="px-3 py-2">
                      {c.status === "paid"
                        ? <span className="text-emerald-300 inline-flex items-center gap-1"><CheckCircle2 className="h-3 w-3" /> {t("affiliates.status_paid")}</span>
                        : <span className="text-amber-300 inline-flex items-center gap-1"><Wallet className="h-3 w-3" /> {t("affiliates.status_pending")}</span>}
                    </td>
                    <td className="px-3 py-2 text-[11px] text-zinc-500 font-mono">{new Date(c.created_at).toLocaleDateString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          )}
        </div>
        </>
      )}
    </div>
  );
}

// ─── Referrals (Filleuls) panel ──────────────────────────────────────────
function ReferralsPanel({ affiliateCode, email }) {
  const { t } = useI18n();
  const [items, setItems] = useState(null);
  const [err, setErr] = useState("");
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/affiliates/${encodeURIComponent(affiliateCode)}/referrals?email=${encodeURIComponent(email)}`);
        setItems(data);
      } catch (e) { setErr(e.response?.data?.detail || t("affiliates.err_generic")); }
    })();
  }, [affiliateCode, email, t]);
  if (err) return <div className="text-xs text-red-300">{err}</div>;
  if (!items) return <div className="text-sm text-zinc-500">{t("affiliates.loading")}</div>;
  return (
    <div className="glass rounded-2xl p-5" data-testid="referrals-panel">
      <h3 className="text-base font-semibold mb-3 inline-flex items-center gap-2"><Users className="h-4 w-4 text-cyan-300" /> {t("affiliates.ref_title")} ({items.length})</h3>
      {items.length === 0 ? (
        <div className="text-sm text-zinc-500 italic">{t("affiliates.ref_empty")}</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-zinc-500 text-xs uppercase tracking-wider">
              <tr>
                <th className="px-3 py-2 text-left">{t("affiliates.ref_col_client")}</th>
                <th className="px-3 py-2 text-left">{t("affiliates.ref_col_plans")}</th>
                <th className="px-3 py-2 text-right">{t("affiliates.ref_col_orders")}</th>
                <th className="px-3 py-2 text-left">{t("affiliates.ref_col_renewal")}</th>
                <th className="px-3 py-2 text-right">{t("affiliates.ref_col_cumul")}</th>
                <th className="px-3 py-2 text-left">{t("affiliates.ref_col_since")}</th>
              </tr>
            </thead>
            <tbody>
              {items.map((r) => (
                <tr key={r.client_email} className="border-t border-white/[0.04]" data-testid={`referral-${r.client_email}`}>
                  <td className="px-3 py-2 text-xs text-zinc-300">{r.client_email}</td>
                  <td className="px-3 py-2 text-xs text-zinc-400">{r.all_plans.slice(0, 3).join(", ")}{r.all_plans.length > 3 ? "…" : ""}</td>
                  <td className="px-3 py-2 text-right font-mono text-xs">{r.orders_count}</td>
                  <td className="px-3 py-2 text-xs">
                    {r.has_renewal
                      ? <span className="text-amber-300 inline-flex items-center gap-1"><CheckCircle2 className="h-3 w-3" /> {t("affiliates.yes")}</span>
                      : <span className="text-zinc-500">{t("affiliates.no")}</span>}
                  </td>
                  <td className="px-3 py-2 text-right font-mono text-emerald-300">{r.total_commission_xof.toLocaleString("fr-FR")} FCFA</td>
                  <td className="px-3 py-2 text-[11px] text-zinc-500 font-mono">{new Date(r.referred_since).toLocaleDateString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ─── Payouts panel ───────────────────────────────────────────────────────
function PayoutsPanel({ affiliateCode, email, pendingXof, reload }) {
  const { t } = useI18n();
  const [items, setItems] = useState(null);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState("");
  const [msg, setMsg] = useState("");
  const [minXof, setMinXof] = useState(0);

  const load = React.useCallback(async () => {
    setErr("");
    try {
      const { data } = await api.get(`/affiliates/${encodeURIComponent(affiliateCode)}/payouts?email=${encodeURIComponent(email)}`);
      setItems(data);
    } catch (e) { setErr(e.response?.data?.detail || t("affiliates.err_generic")); }
  }, [affiliateCode, email, t]);
  useEffect(() => { load(); }, [load]);

  // Fetch public settings (payout min threshold)
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/settings/public");
        setMinXof(parseInt(data?.payout_min_xof || 0, 10));
      } catch (e) {
        // Public settings fetch is non-blocking: the UI falls back to minXof=0
        // (no minimum threshold displayed) if /settings/public is unavailable.
        console.warn("[affiliates] public settings fetch failed (non-blocking):", e?.response?.status);
      }
    })();
  }, []);

  const submit = async () => {
    setBusy(true); setMsg("");
    try {
      await api.post(`/affiliates/${encodeURIComponent(affiliateCode)}/payouts`, { email, note });
      setNote(""); setMsg(t("affiliates.po_success"));
      await load(); reload && reload();
    } catch (e) { setMsg(e.response?.data?.detail || t("affiliates.err_generic")); }
    finally { setBusy(false); }
  };

  const hasActive = items?.some((p) => ["pending", "processing"].includes(p.status));
  const belowThreshold = minXof > 0 && (pendingXof || 0) < minXof;
  const disabled = busy || hasActive || !pendingXof || belowThreshold;

  return (
    <div className="space-y-5" data-testid="payouts-panel">
      <div className="glass rounded-2xl p-5">
        <div className="flex items-start justify-between flex-wrap gap-3 mb-3">
          <div>
            <h3 className="text-base font-semibold inline-flex items-center gap-2"><Wallet className="h-4 w-4 text-cyan-300" /> {t("affiliates.po_title")}</h3>
            <p className="text-xs text-zinc-500 mt-1">
              {t("affiliates.po_balance")} <b className="text-emerald-300 font-mono text-base">{(pendingXof || 0).toLocaleString("fr-FR")} FCFA</b>.
              {" "}{t("affiliates.po_processing")} <b>{t("affiliates.po_processing_days")}</b>.
            </p>
            {minXof > 0 && (
              <p className="text-[11px] text-zinc-500 mt-1" data-testid="payout-min-threshold-info">
                {t("affiliates.po_threshold")} <b className="text-cyan-300 font-mono">{minXof.toLocaleString("fr-FR").replaceAll(",", " ")} FCFA</b>.
                {belowThreshold && (
                  <span className="text-amber-300">{t("affiliates.po_threshold_short_pre")}{(minXof - (pendingXof || 0)).toLocaleString("fr-FR").replaceAll(",", " ")}{t("affiliates.po_threshold_short_post")}</span>
                )}
              </p>
            )}
          </div>
        </div>
        <div className="grid sm:grid-cols-[1fr_auto] gap-3 items-end">
          <label className="block">
            <span className="text-xs text-zinc-400 mb-1 block">{t("affiliates.po_note")}</span>
            <input type="text" value={note} onChange={(e) => setNote(e.target.value)}
              className="input" maxLength={300} placeholder={t("affiliates.po_note_ph")}
              data-testid="payout-note" />
          </label>
          <button onClick={submit} disabled={disabled}
            title={belowThreshold ? `${t("affiliates.po_threshold")} ${minXof.toLocaleString("fr-FR")} FCFA` : ""}
            className="btn-primary px-5 py-2.5 rounded-lg text-sm inline-flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
            data-testid="payout-submit">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Wallet className="h-4 w-4" />}
            {hasActive ? t("affiliates.po_btn_pending") : belowThreshold ? t("affiliates.po_btn_insufficient") : t("affiliates.po_btn_request")}
          </button>
        </div>
        {msg && <div className="text-xs text-emerald-300 mt-3">{msg}</div>}
        {err && <div className="text-xs text-red-300 mt-3">{err}</div>}
      </div>

      <div className="glass rounded-2xl p-5">
        <h3 className="text-base font-semibold mb-3">{t("affiliates.po_history")}</h3>
        {items && items.length === 0 && <div className="text-sm text-zinc-500 italic">{t("affiliates.po_history_empty")}</div>}
        {items && items.length > 0 && (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-zinc-500 text-xs uppercase tracking-wider">
                <tr>
                  <th className="px-3 py-2 text-left">{t("affiliates.col_date")}</th>
                  <th className="px-3 py-2 text-right">{t("affiliates.po_col_amount")}</th>
                  <th className="px-3 py-2 text-left">{t("affiliates.po_col_method")}</th>
                  <th className="px-3 py-2 text-left">{t("affiliates.col_status")}</th>
                  <th className="px-3 py-2 text-right">{t("affiliates.po_col_wait")}</th>
                  <th className="px-3 py-2 text-left">{t("affiliates.po_col_paid_ref")}</th>
                </tr>
              </thead>
              <tbody>
                {items.map((p) => (
                  <tr key={p.id} className="border-t border-white/[0.04]" data-testid={`payout-${p.id}`}>
                    <td className="px-3 py-2 text-[11px] font-mono text-zinc-500">{new Date(p.created_at).toLocaleDateString()}</td>
                    <td className="px-3 py-2 text-right font-mono">{p.amount_xof.toLocaleString("fr-FR")}</td>
                    <td className="px-3 py-2 text-xs">{p.payment_method === "usdt" ? "USDT" : "Mobile"}</td>
                    <td className="px-3 py-2">
                      <PayoutStatusBadge status={p.status} />
                    </td>
                    <td className={`px-3 py-2 text-right font-mono text-xs ${p.overdue ? "text-red-300" : "text-zinc-400"}`}>
                      {p.days_waiting} / {p.sla_days}j
                    </td>
                    <td className="px-3 py-2 text-[11px] font-mono text-zinc-500 break-all">{p.paid_ref || (p.status === "rejected" ? p.reason : "—")}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

export function PayoutStatusBadge({ status }) {
  const { t } = useI18n();
  const styles = {
    pending:    { cls: "bg-amber-400/15 text-amber-300",   label: t("affiliates.po_st_pending") },
    processing: { cls: "bg-cyan-400/15 text-cyan-300",     label: t("affiliates.po_st_processing") },
    paid:       { cls: "bg-emerald-400/15 text-emerald-300", label: t("affiliates.po_st_paid") },
    rejected:   { cls: "bg-red-400/15 text-red-300",       label: t("affiliates.po_st_rejected") },
  };
  const s = styles[status] || { cls: "bg-zinc-700 text-zinc-300", label: status };
  return <span className={`text-[10px] uppercase font-mono px-1.5 py-0.5 rounded ${s.cls}`}>{s.label}</span>;
}

// ─── Marketing sub-panel (inside the affiliate dashboard) ────────────────
function MarketingPanel({ affiliateCode, email }) {
  const { t } = useI18n();
  const [info, setInfo] = useState(null);
  const [err, setErr] = useState("");
  const [showForm, setShowForm] = useState(false);
  const load = React.useCallback(async () => {
    setErr("");
    try {
      const { data } = await api.get(`/affiliates/${encodeURIComponent(affiliateCode)}/promos?email=${encodeURIComponent(email)}`);
      setInfo(data);
    } catch (e) { setErr(e.response?.data?.detail || t("affiliates.err_generic")); }
  }, [affiliateCode, email, t]);
  useEffect(() => { load(); }, [load]);

  const onCreated = () => { setShowForm(false); load(); };

  const toggle = async (promoCode, active) => {
    try {
      await api.post(`/affiliates/${encodeURIComponent(affiliateCode)}/promos/${encodeURIComponent(promoCode)}/toggle-active`, { email, active });
      load();
    } catch (e) { alert(e.response?.data?.detail || t("affiliates.err_generic")); }
  };

  const del = async (promoCode) => {
    if (!window.confirm(t("affiliates.mk_confirm_del").replace("{code}", promoCode))) return;
    try {
      await api.delete(`/affiliates/${encodeURIComponent(affiliateCode)}/promos/${encodeURIComponent(promoCode)}?email=${encodeURIComponent(email)}`);
      load();
    } catch (e) { alert(e.response?.data?.detail || t("affiliates.err_generic")); }
  };

  const base = info?.base_rate_percent ?? 10;

  return (
    <div className="space-y-5" data-testid="marketing-panel">
      <div className="glass rounded-2xl p-5">
        <div className="flex items-start justify-between flex-wrap gap-3 mb-2">
          <div>
            <h3 className="text-base font-semibold mb-1 flex items-center gap-2"><Tag className="h-4 w-4 text-cyan-300" /> {t("affiliates.mk_title")}</h3>
            <p className="text-xs text-zinc-500 leading-relaxed max-w-xl">
              {t("affiliates.mk_lead_pre")}<b>{t("affiliates.mk_lead_deducted")}</b>{t("affiliates.mk_lead_mid")}<b>{t("affiliates.mk_lead_100")}</b>{t("affiliates.mk_lead_post")}
            </p>
            <p className="text-[11px] text-zinc-500 mt-2">
              {t("affiliates.mk_current_rate")} <b className="text-cyan-300 font-mono">{base}%</b>{t("affiliates.mk_max_discount")} <b className="text-cyan-300 font-mono">{base}%</b>
            </p>
          </div>
          <button onClick={() => setShowForm((v) => !v)}
            className="btn-primary px-4 py-2 rounded-lg text-sm inline-flex items-center gap-2"
            data-testid="marketing-new-btn">
            <Tag className="h-4 w-4" /> {showForm ? t("affiliates.mk_cancel") : t("affiliates.mk_new")}
          </button>
        </div>

        {showForm && (
          <PromoForm
            affiliateCode={affiliateCode}
            email={email}
            maxDiscount={base}
            onCreated={onCreated}
          />
        )}
      </div>

      <div className="glass rounded-2xl p-5">
        {err && <div className="text-xs text-red-300 mb-3">{err}</div>}
        {info && info.promos.length === 0 && (
          <div className="text-sm text-zinc-500 italic">{t("affiliates.mk_empty")}</div>
        )}
        {info && info.promos.length > 0 && (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-zinc-500 text-xs uppercase tracking-wider">
                <tr>
                  <th className="px-3 py-2 text-left">{t("affiliates.mk_col_code")}</th>
                  <th className="px-3 py-2 text-left">{t("affiliates.mk_col_discount")}</th>
                  <th className="px-3 py-2 text-left">{t("affiliates.mk_col_validity")}</th>
                  <th className="px-3 py-2 text-right">{t("affiliates.mk_col_uses")}</th>
                  <th className="px-3 py-2 text-left">{t("affiliates.col_status")}</th>
                  <th className="px-3 py-2 text-left">{t("affiliates.mk_col_actions")}</th>
                </tr>
              </thead>
              <tbody>
                {info.promos.map((p) => (
                  <tr key={p.id} className="border-t border-white/[0.04]" data-testid={`marketing-promo-${p.code}`}>
                    <td className="px-3 py-2 font-mono text-xs text-cyan-300">{p.code}</td>
                    <td className="px-3 py-2 text-sm font-semibold text-emerald-300">−{p.discount_percent}%</td>
                    <td className="px-3 py-2 text-[11px] text-zinc-500 font-mono">
                      {new Date(p.valid_from).toLocaleDateString()} → {new Date(p.valid_to).toLocaleDateString()}
                    </td>
                    <td className="px-3 py-2 text-right font-mono text-xs">{p.used_count}{p.max_uses ? `/${p.max_uses}` : ""}</td>
                    <td className="px-3 py-2 text-xs">
                      {p.status === "active"
                        ? <span className="text-emerald-300 inline-flex items-center gap-1"><CheckCircle2 className="h-3 w-3" /> {t("affiliates.mk_active")}</span>
                        : <span className="text-zinc-500 inline-flex items-center gap-1"><XCircle className="h-3 w-3" /> {t("affiliates.mk_inactive")}</span>}
                    </td>
                    <td className="px-3 py-2">
                      <div className="flex gap-1.5">
                        <button onClick={() => toggle(p.code, p.status !== "active")}
                          className="text-xs px-2 py-1 rounded bg-white/[0.04] hover:bg-white/[0.08]"
                          data-testid={`marketing-toggle-${p.code}`}>
                          {p.status === "active" ? t("affiliates.mk_btn_deactivate") : t("affiliates.mk_btn_activate")}
                        </button>
                        <button onClick={() => del(p.code)}
                          className="text-xs px-2 py-1 rounded bg-red-400/10 hover:bg-red-400/20 text-red-300"
                          data-testid={`marketing-delete-${p.code}`}>
                          {t("affiliates.mk_btn_delete")}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

function PromoForm({ affiliateCode, email, maxDiscount, onCreated }) {
  const { t } = useI18n();
  const todayIso = new Date().toISOString().slice(0, 10);
  const in30Iso = new Date(Date.now() + 30 * 24 * 3600 * 1000).toISOString().slice(0, 10);
  const [form, setForm] = useState({
    code: "", discount_percent: Math.min(5, maxDiscount),
    valid_from: todayIso, valid_to: in30Iso, max_uses: "",
  });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setErr(""); setBusy(true);
    try {
      await api.post(`/affiliates/${encodeURIComponent(affiliateCode)}/promos`, {
        email,
        code: form.code.trim().toUpperCase() || undefined,
        discount_percent: Number(form.discount_percent),
        valid_from: new Date(form.valid_from + "T00:00:00Z").toISOString(),
        valid_to: new Date(form.valid_to + "T23:59:59Z").toISOString(),
        max_uses: form.max_uses ? Number(form.max_uses) : null,
      });
      onCreated();
    } catch (e2) { setErr(e2.response?.data?.detail || t("affiliates.err_generic")); }
    finally { setBusy(false); }
  };

  return (
    <form onSubmit={submit} className="mt-4 pt-4 border-t border-white/[0.06] grid sm:grid-cols-2 gap-3" data-testid="marketing-form">
      <Field label={t("affiliates.mk_f_code")}>
        <input type="text" value={form.code} onChange={update("code")}
          className="input font-mono uppercase" maxLength={30}
          placeholder={t("affiliates.mk_f_code_ph")} data-testid="marketing-code" />
      </Field>
      <Field label={`${t("affiliates.mk_f_discount")} ${maxDiscount}%`} required>
        <input type="number" min={1} max={maxDiscount} value={form.discount_percent}
          onChange={update("discount_percent")} required className="input" data-testid="marketing-discount" />
      </Field>
      <Field label={t("affiliates.mk_f_from")} required>
        <input type="date" value={form.valid_from} onChange={update("valid_from")} required className="input" data-testid="marketing-from" />
      </Field>
      <Field label={t("affiliates.mk_f_to")} required>
        <input type="date" value={form.valid_to} onChange={update("valid_to")} required className="input" data-testid="marketing-to" />
      </Field>
      <Field label={t("affiliates.mk_f_uses")}>
        <input type="number" min={1} value={form.max_uses} onChange={update("max_uses")}
          className="input" placeholder={t("affiliates.mk_f_uses_ph")} data-testid="marketing-maxuses" />
      </Field>
      <div className="sm:col-span-2 flex items-center justify-between">
        {err && <div className="text-xs text-red-300">{err}</div>}
        <button type="submit" disabled={busy}
          className="btn-primary px-4 py-2.5 rounded-lg text-sm inline-flex items-center gap-2 ml-auto"
          data-testid="marketing-submit">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Tag className="h-4 w-4" />}
          {t("affiliates.mk_create")}
        </button>
      </div>
    </form>
  );
}

function SuccessCard({ code, message }) {
  const { t } = useI18n();
  const [copiedCode, setCopiedCode] = useState(false);
  const [copiedLink, setCopiedLink] = useState(false);
  const origin = typeof window !== "undefined" ? window.location.origin : "";
  const shareLink = `${origin}/?ref=${code}`;
  const copyCode = () => { navigator.clipboard.writeText(code); setCopiedCode(true); setTimeout(() => setCopiedCode(false), 1500); };
  const copyLink = () => { navigator.clipboard.writeText(shareLink); setCopiedLink(true); setTimeout(() => setCopiedLink(false), 1500); };
  return (
    <div className="glass rounded-2xl p-6 sm:p-8 max-w-xl" data-testid="affiliate-success">
      <div className="flex items-center justify-center w-12 h-12 rounded-full bg-emerald-400/20 border border-emerald-400/40 mb-3 mx-auto">
        <CheckCircle2 className="h-6 w-6 text-emerald-300" />
      </div>
      <h2 className="text-xl font-semibold mb-2 text-center">{t("affiliates.su_title")}</h2>
      <p className="text-xs text-zinc-500 mb-5 text-center">{message}</p>
      <div className="bg-cyan-400/5 border border-cyan-400/30 rounded-xl p-4 mb-3">
        <div className="text-[11px] uppercase tracking-wider text-cyan-300 mb-1">{t("affiliates.su_code_label")}</div>
        <div className="text-2xl font-mono font-bold tracking-wider text-center" data-testid="affiliate-code">{code}</div>
      </div>
      <div className="bg-white/[0.02] border border-white/[0.06] rounded-xl p-3 mb-4">
        <div className="text-[11px] uppercase tracking-wider text-zinc-500 mb-1.5">{t("affiliates.su_link_label")}</div>
        <code className="block text-xs font-mono text-zinc-200 bg-black/30 px-2 py-1.5 rounded break-all" data-testid="affiliate-share-link">
          {shareLink}
        </code>
      </div>
      <div className="grid sm:grid-cols-2 gap-2">
        <button onClick={copyCode}
          className="btn-ghost py-2.5 rounded-lg text-sm inline-flex items-center justify-center gap-2"
          data-testid="affiliate-copy-code">
          <Copy className="h-4 w-4" /> {copiedCode ? t("affiliates.su_copied") : t("affiliates.su_copy_code")}
        </button>
        <button onClick={copyLink}
          className="btn-primary py-2.5 rounded-lg text-sm inline-flex items-center justify-center gap-2"
          data-testid="affiliate-copy-link">
          <LinkIcon className="h-4 w-4" /> {copiedLink ? t("affiliates.su_copied") : t("affiliates.su_copy_link")}
        </button>
      </div>
      <p className="text-[11px] text-zinc-500 mt-4 leading-relaxed text-center">
        {t("affiliates.su_share_note")}
      </p>
    </div>
  );
}

function Field({ label, required, cols = 1, children }) {
  return (
    <label className={`block ${cols === 2 ? "sm:col-span-2" : ""}`}>
      <span className="text-xs text-zinc-400 mb-1.5 block">
        {label}{required && " *"}
      </span>
      {children}
    </label>
  );
}

function Stat({ label, value, icon: Icon, accent }) {
  return (
    <div className="glass rounded-xl p-4">
      <div className="flex items-center justify-between mb-2">
        <span className="text-[11px] uppercase tracking-[0.15em] text-zinc-500">{label}</span>
        <Icon className={`h-4 w-4 ${accent}`} />
      </div>
      <div className="text-xl font-bold tracking-tighter font-mono">{value}</div>
    </div>
  );
}

function CopyCodeBtn({ code }) {
  const { t } = useI18n();
  const [copied, setCopied] = useState(false);
  const copy = () => { navigator.clipboard.writeText(code); setCopied(true); setTimeout(() => setCopied(false), 1500); };
  return (
    <button onClick={copy} className="btn-primary px-4 py-2.5 rounded-lg text-sm inline-flex items-center gap-2"
      data-testid="dash-copy-code">
      <Copy className="h-4 w-4" /> {copied ? t("affiliates.copied") : t("affiliates.copy_my_code")}
    </button>
  );
}

function CopyLinkBtn({ link }) {
  const { t } = useI18n();
  const [copied, setCopied] = useState(false);
  const copy = () => { navigator.clipboard.writeText(link); setCopied(true); setTimeout(() => setCopied(false), 1500); };
  return (
    <button onClick={copy} className="btn-primary shrink-0 px-4 py-2 rounded-lg text-sm inline-flex items-center justify-center gap-2"
      data-testid="dash-copy-link">
      <LinkIcon className="h-4 w-4" /> {copied ? t("affiliates.copied") : t("affiliates.copy")}
    </button>
  );
}
